import React, { useState } from 'react';
import { Building2, User, Mail, Phone, MapPin, Wrench, Eye, EyeOff, DollarSign, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import ContractorDashboard from '@/components/ContractorDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_AREAS = [
  'New Orleans, LA',
  'Metairie, LA',
  'Kenner, LA',
  'Slidell, LA',
  'Chalmette, LA',
  'Covington, LA',
  'Mandeville, LA',
  'Hammond, LA',
];

const SERVICES = [
  { id: 'ac-repair', label: 'AC Repair', price: 35 },
  { id: 'ac-installation', label: 'AC Installation', price: 75 },
  { id: 'heating-repair', label: 'Heating Repair', price: 35 },
  { id: 'heating-installation', label: 'Heating Installation', price: 75 },
  { id: 'duct-cleaning', label: 'Duct Cleaning', price: 30 },
  { id: 'maintenance', label: 'Maintenance', price: 25 },
  { id: 'emergency', label: 'Emergency Service', price: 85 },
];

const LEAD_COST = 25; // Base cost - actual varies by service

const getLeadPrice = (serviceType) => {
  const service = SERVICES.find(s => s.id === serviceType);
  return service ? service.price : 35;
};

const ContractorPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [contractor, setContractor] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const [registerData, setRegisterData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    password: '',
    service_areas: [],
    services_offered: [],
  });

  React.useEffect(() => {
    const saved = localStorage.getItem('contractor');
    if (saved) {
      setContractor(JSON.parse(saved));
      setIsLoggedIn(true);
    }
    
    // Check for password reset token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset');
    if (token) {
      verifyResetToken(token);
    }
    // Intentional: runs once on mount to check stored session & URL params
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyResetToken = async (token) => {
    try {
      const response = await axios.get(`${API}/contractors/verify-reset-token/${token}`);
      if (response.data.success) {
        setResetToken(token);
        setResetEmail(response.data.email);
      }
    } catch (error) {
      toast.error('Invalid or expired reset link');
      window.history.replaceState({}, '', '/contractors');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contractors/forgot-password`, { email: resetEmail });
      toast.success(response.data.message);
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contractors/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success(response.data.message);
      setResetToken(null);
      setNewPassword('');
      setConfirmPassword('');
      window.history.replaceState({}, '', '/contractors');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contractors/login`, loginData);
      if (response.data.success) {
        setContractor(response.data.contractor);
        setIsLoggedIn(true);
        localStorage.setItem('contractor', JSON.stringify(response.data.contractor));
        toast.success('Welcome back!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the lead policy terms');
      return;
    }
    if (registerData.service_areas.length === 0) {
      toast.error('Please select at least one service area');
      return;
    }
    if (registerData.services_offered.length === 0) {
      toast.error('Please select at least one service you offer');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/contractors/register`, registerData);
      if (response.data.success) {
        toast.success('Registration successful! Please log in.');
        setIsRegistering(false);
        setLoginData({ email: registerData.email, password: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setContractor(null);
    localStorage.removeItem('contractor');
    toast.success('Logged out successfully');
  };

  const toggleServiceArea = (area) => {
    setRegisterData((prev) => ({
      ...prev,
      service_areas: prev.service_areas.includes(area)
        ? prev.service_areas.filter((a) => a !== area)
        : [...prev.service_areas, area],
    }));
  };

  const toggleService = (serviceId) => {
    setRegisterData((prev) => ({
      ...prev,
      services_offered: prev.services_offered.includes(serviceId)
        ? prev.services_offered.filter((s) => s !== serviceId)
        : [...prev.services_offered, serviceId],
    }));
  };

  if (isLoggedIn && contractor) {
    return <ContractorDashboard contractor={contractor} onLogout={handleLogout} />;
  }

  // Password Reset Form
  if (resetToken) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#0F172A] rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">N</span>
              </div>
              <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                NOLA HVAC Connect
              </span>
            </div>
          </div>
        </header>

        <main className="max-w-md mx-auto px-6 py-12">
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Reset Your Password
              </h1>
              <p className="text-slate-500 mt-2">Enter your new password below</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1.5"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#F97316] hover:bg-[#EA580C] h-12"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F172A] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">N</span>
            </div>
            <div>
              <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                NOLA HVAC Connect
              </span>
              <p className="text-xs text-slate-500">Contractor Portal</p>
            </div>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Pricing & Info Banner */}
        <div className="bg-gradient-to-r from-[#0F172A] to-slate-800 rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Grow Your HVAC Business
              </h2>
              <p className="text-slate-300">
                Get exclusive access to qualified homeowner leads in your service area
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
              <p className="text-sm text-slate-300">Lead Pricing</p>
              <p className="text-3xl font-bold text-[#F97316]">$25-$85</p>
              <p className="text-xs text-slate-400">Varies by service type</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Login/Register Form */}
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
            {!isRegistering ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Building2 className="w-8 h-8 text-[#0EA5E9]" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Contractor Login
                  </h1>
                  <p className="text-slate-500 mt-2">Access leads in your service area</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="mt-1.5"
                      data-testid="contractor-email"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="mt-1.5 pr-10"
                        data-testid="contractor-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="contractor-login-btn"
                    className="w-full bg-[#0F172A] hover:bg-slate-800 h-12"
                  >
                    {isLoading ? 'Logging in...' : 'Log In'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-slate-500">
                    New contractor?{' '}
                    <button
                      onClick={() => setIsRegistering(true)}
                      className="text-[#0EA5E9] font-semibold hover:underline"
                      data-testid="register-link"
                    >
                      Register here
                    </button>
                  </p>
                  <button
                    onClick={() => setShowForgotPassword(true)}
                    className="text-slate-500 text-sm hover:underline mt-2"
                  >
                    Forgot password?
                  </button>
                </div>

                {/* Forgot Password Modal */}
                {showForgotPassword && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-6 rounded-2xl max-w-md w-full">
                      <h3 className="text-xl font-bold text-[#0F172A] mb-4">Reset Password</h3>
                      <p className="text-slate-500 text-sm mb-4">
                        Enter your email and we'll send you a reset link.
                      </p>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setShowForgotPassword(false);
                              setResetEmail('');
                            }}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 bg-[#F97316] hover:bg-[#EA580C]"
                          >
                            {isLoading ? 'Sending...' : 'Send Reset Link'}
                          </Button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#F97316]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Wrench className="w-8 h-8 text-[#F97316]" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Join Our Network
                  </h1>
                  <p className="text-slate-500 mt-2">Register to receive HVAC leads</p>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={registerData.company_name}
                      onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                      className="mt-1.5"
                      placeholder="Calloway & Sons A/C and Heating"
                      data-testid="reg-company"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      value={registerData.contact_name}
                      onChange={(e) => setRegisterData({ ...registerData, contact_name: e.target.value })}
                      className="mt-1.5"
                      placeholder="John Calloway"
                      data-testid="reg-contact"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="reg_email">Email</Label>
                      <Input
                        id="reg_email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="mt-1.5"
                        data-testid="reg-email"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg_phone">Phone</Label>
                      <Input
                        id="reg_phone"
                        type="tel"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                        className="mt-1.5"
                        data-testid="reg-phone"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="reg_password">Password</Label>
                    <Input
                      id="reg_password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="mt-1.5"
                      data-testid="reg-password"
                      required
                      minLength={6}
                    />
                  </div>

                  <div>
                    <Label className="mb-2 block">Service Areas</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-3 bg-slate-50 rounded-lg">
                      {SERVICE_AREAS.map((area) => (
                        <label key={area} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={registerData.service_areas.includes(area)}
                            onCheckedChange={() => toggleServiceArea(area)}
                          />
                          {area}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Services Offered</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                      {SERVICES.map((service) => (
                        <label key={service.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={registerData.services_offered.includes(service.id)}
                            onCheckedChange={() => toggleService(service.id)}
                          />
                          {service.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Terms Agreement */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox
                        checked={agreedToTerms}
                        onCheckedChange={setAgreedToTerms}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-slate-600">
                        I agree to the <span className="font-semibold text-[#0F172A]">Lead Policy Terms</span> including 
                        the variable pricing ($25-$85 per lead based on service type) and understand what qualifies as a valid lead.
                      </span>
                    </label>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="contractor-register-btn"
                    className="w-full bg-[#F97316] hover:bg-[#EA580C] h-12"
                  >
                    {isLoading ? 'Registering...' : 'Register'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-slate-500">
                    Already registered?{' '}
                    <button
                      onClick={() => setIsRegistering(false)}
                      className="text-[#0EA5E9] font-semibold hover:underline"
                    >
                      Log in here
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Lead Policy Info */}
          <div className="space-y-6">
            {/* What is a Lead */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  What Counts as a Lead
                </h3>
              </div>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Homeowner with a verified phone number and email</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Located within your selected service areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Actively seeking HVAC services (repair, install, maintenance)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>First-time inquiry (not a duplicate)</span>
                </li>
              </ul>
            </div>

            {/* What is NOT a Lead */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  What Does NOT Count
                </h3>
              </div>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Wrong or disconnected phone numbers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Customer outside your service area</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Spam or fake submissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">✗</span>
                  <span>Commercial properties (residential only)</span>
                </li>
              </ul>
              <p className="text-xs text-slate-500 mt-4 p-3 bg-slate-50 rounded-lg">
                <strong>Refund Policy:</strong> If a lead doesn't meet these criteria, contact us within 48 hours for a credit or refund.
              </p>
            </div>

            {/* How it Works */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#0EA5E9]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Lead Pricing by Service
                </h3>
              </div>
              <div className="space-y-2 mb-4">
                {SERVICES.map((service) => (
                  <div key={service.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-600">{service.label}</span>
                    <span className="font-bold text-[#0F172A]">${service.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                Prices reflect marketing acquisition costs and lead value. Emergency & installation leads convert at higher rates.
              </p>
            </div>

            {/* How it Works Steps */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  How It Works
                </h3>
              </div>
              <ol className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>New leads appear in your dashboard (contact info hidden)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Click "Claim Lead" to get exclusive access (price shown per lead)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Full contact info revealed - call within 5 mins for best results</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Close the deal and grow your business!</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContractorPortal;
